import React, { useCallback, useState } from 'react'
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Typography,
  Box,
  IconButton,
} from '@mui/material'

import { PropertyList } from '../../../../../../../components/PropertyList'
import { PropertyListItem } from '../../../../../../../components/PropertyListItem'
import CircularLoading from '../../../../../../../components/CircularProgress'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
} from '../../../../../../../types/order'
import OrderTag from '../../../../../../../components/OrderTag'
import dayjs from 'dayjs'
import {
  DATE_FORMAT,
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { IDistributionChannel } from '../../../../../../../types/distribution-channel'
import EditIcon from '@mui/icons-material/Edit'
import { message, Table, TableColumnsType } from 'antd'
import { ILead } from '../../../../../../../types/lead'
import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import {
  ETrackingNotificationsStatusCalled,
  ETrackingNotificationsStatusFollow,
  ITrackingNotifications,
} from '../../../../../../../types/trackingNotification'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import LeadCreateTrackingNotificationModal from './LeadCreateTrackingNotificationModal'
import { Tag } from '../../../../../../../components/Tag'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AddIcCallIcon from '@mui/icons-material/AddIcCall'
import CallIcon from '@mui/icons-material/Call'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'

export interface IOderDetailCard {
  status?: EOrderStatus
  quantity?: number
  commission?: number
  paymentMethod?: EOrderType
  items?: IOrderItem[]
  actor?: string
  distribution?: IDistributionChannel
  createdAt?: string
  updatedAt?: string
  note?: string
}

interface FilterGroupProps {
  isLoading: boolean
  data?: ILead
  onUpdateLeadSuccess: () => void
  isFinished?: boolean
}

const LeadCustomerTrackingNotificationListDetailCard = ({
  isLoading,
  data,
  onUpdateLeadSuccess,
  isFinished,
}: FilterGroupProps) => {
  const [
    isModalCreateTrackingNotification,
    setIsModalCreateTrackingNotification,
  ] = useState<boolean>(false)
  const trackingNotificationList = useAxios<
    PaginationResponseInterface<ITrackingNotifications>
  >({
    method: 'GET',
    url: `/lead/tracking-notification`,
    params: {
      leadObjectId: data?.objectId,
      page: 1,
      perPage: 1000,
    },
  })

  const columns: TableColumnsType<ITrackingNotifications & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 50,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      width: 120,
      align: 'center' as const,
      render: (expiredAt: string) => {
        return (
          <Box>
            <Tag
              color={dayjs().isAfter(dayjs(expiredAt)) ? 'error' : 'success'}
            >
              {dayjs().isAfter(dayjs(expiredAt)) ? 'หมดอายุ' : 'ใช้งานได้'}
            </Tag>
            <Typography
              sx={{ mt: 1 }}
              variant="subtitle2"
              fontSize={11}
              color="text.secondary"
            >
              {dayjs(expiredAt).format(ONLY_DATE_FORMAT)}
              <br />
              {dayjs(expiredAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'การติดตาม',
      dataIndex: 'statusFollow',
      key: 'statusFollow',
      align: 'center' as const,
      render: (
        statusFollow: ETrackingNotificationsStatusFollow,
        trackingNotification: ITrackingNotifications,
      ) => {
        return (
          <>
            <Button
              disabled={isFinished}
              variant="outlined"
              size="small"
              startIcon={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT ? (
                  <AccessTimeIcon color={'warning'} />
                ) : (
                  <CheckCircleOutlineIcon color={'info'} />
                )
              }
              color={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'warning'
                  : 'info'
              }
              onClick={() =>
                changeStatusFollow(
                  statusFollow === ETrackingNotificationsStatusFollow.DONE
                    ? ETrackingNotificationsStatusFollow.WAIT
                    : ETrackingNotificationsStatusFollow.DONE,
                  trackingNotification?.objectId!,
                )
              }
            >
              <Typography variant="subtitle2">
                {statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'ยังไม่ติดตาม'
                  : 'ติดตามแล้ว'}
              </Typography>
            </Button>
          </>
        )
      },
    },
    {
      title: 'การโทร',
      dataIndex: 'statusCalled',
      key: 'statusCalled',
      align: 'center' as const,
      render: (
        statusCalled: ETrackingNotificationsStatusCalled,
        trackingNotification: ITrackingNotifications,
      ) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {statusCalled === ETrackingNotificationsStatusCalled.NOT_CALLED ? (
              <>
                <Button
                  disabled={isFinished}
                  variant="outlined"
                  size="small"
                  startIcon={
                    <CallIcon
                      sx={{ transform: 'rotate(-45deg)' }}
                      color={'success'}
                    />
                  }
                  color={'success'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  โทรแล้ว
                </Button>
                <Button
                  disabled={isFinished}
                  variant="outlined"
                  size="small"
                  startIcon={
                    <AddIcCallIcon
                      sx={{ transform: 'rotate(-45deg)' }}
                      color={'error'}
                    />
                  }
                  color={'error'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.CANCEL_CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  ไม่รับสาย
                </Button>
              </>
            ) : (
              <>
                <Tag
                  color={
                    statusCalled ===
                    ETrackingNotificationsStatusCalled.CANCEL_CALLED
                      ? 'error'
                      : 'success'
                  }
                >
                  <Typography variant="subtitle2">
                    {statusCalled ===
                    ETrackingNotificationsStatusCalled.CANCEL_CALLED
                      ? 'ไม่รับสาย'
                      : 'โทรแล้ว'}
                  </Typography>
                </Tag>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isFinished}
                  fullWidth
                  startIcon={<AccessTimeIcon color={'info'} />}
                  color={'info'}
                  onClick={() =>
                    changeStatusCall(
                      ETrackingNotificationsStatusCalled.NOT_CALLED,
                      trackingNotification?.objectId!,
                    )
                  }
                >
                  ยกเลิก
                </Button>
              </>
            )}
          </Box>
        )
      },
    },
    {
      title: 'เพิ่มโดย',
      dataIndex: 'actor',
      key: 'actor',
      align: 'center' as const,
      render: (actor: string) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {actor}
          </Typography>
        )
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center' as const,
      render: (_: undefined, trackingNotification: ITrackingNotifications) => {
        return (
          <>
            {!dayjs().isAfter(dayjs(trackingNotification.expiredAt)) && (
              <IconButton
                disabled={isFinished}
                onClick={() => {
                  deleteTrackingNotification(trackingNotification.objectId!)
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        )
      },
    },
  ]

  const onUpdateTrackingNotificationListSuccess = useCallback(async () => {
    try {
      await trackingNotificationList.mutate()
    } catch (error) {
      message.error('ไม่สามารถดึงข้อมูล tracking notification ได้')
    }
  }, [trackingNotificationList])

  const changeStatusFollow = async (
    status: ETrackingNotificationsStatusFollow,
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.put(
        `/lead/change-status-tracking-notification-follow/${trackingNotificationObjectId}`,
        {
          status,
        },
      )
      message.success('แก้ไขสถานะสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const changeStatusCall = async (
    status: ETrackingNotificationsStatusCalled,
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.put(
        `/lead/change-status-tracking-notification-called/${trackingNotificationObjectId}`,
        {
          status,
        },
      )
      message.success('แก้ไขสถานะสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const deleteTrackingNotification = async (
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.delete(
        `/lead/remove-tracking-notification/${trackingNotificationObjectId}`,
      )
      message.success('ลบสำเร็จ')
      onUpdateTrackingNotificationListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <>
      <LeadCreateTrackingNotificationModal
        isOpen={isModalCreateTrackingNotification}
        onClose={(visible: boolean) =>
          setIsModalCreateTrackingNotification(visible)
        }
        onUpdateTrackingNotificationListSuccess={
          onUpdateTrackingNotificationListSuccess
        }
        leadObjectId={data?.objectId!}
      />
      <CircularLoading isLoading={isLoading} />
      <Card sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            lg={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              รายการแจ้งเตือนติดตามลูกค้า
            </Typography>
            {data?.cancelAt === null && !isFinished && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => setIsModalCreateTrackingNotification(true)}
              >
                เพิ่มการติดตามใหม่
              </Button>
            )}
          </Grid>
          <Grid item xs={12} lg={12}>
            <Table
              size={'middle'}
              columns={columns}
              scroll={{
                x: columns.reduce(
                  (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                  0,
                ),
              }}
              dataSource={trackingNotificationList?.data?.records?.map(
                (
                  trackingNotification: ITrackingNotifications,
                  index: number,
                ) => {
                  return {
                    ...trackingNotification,
                    key: index,
                    numberRow:
                      index +
                      1 +
                      (trackingNotificationList?.data?.page! - 1) *
                        trackingNotificationList?.data?.perPage!,
                  }
                },
              )}
              bordered
              pagination={false}
            />
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

export default LeadCustomerTrackingNotificationListDetailCard
